'use client'

import { useEffect } from 'react'
import { useTranslations } from 'next-intl'
import { CALENDLY_URL } from '@/lib/site'
import type { Locale } from '@/types/site'

type ToolResult = { content: { type: 'text'; text: string }[] }
type ToolAnnotations = {
  title?: string
  readOnlyHint?: boolean
  destructiveHint?: boolean
  idempotentHint?: boolean
  openWorldHint?: boolean
}
type WebMcpTool = {
  name: string
  description: string
  inputSchema: Record<string, unknown>
  annotations?: ToolAnnotations
  execute: (args: Record<string, unknown>) => Promise<ToolResult>
}
interface ModelContextLike {
  registerTool: (tool: WebMcpTool, options?: { signal?: AbortSignal }) => Promise<unknown>
}

declare global {
  interface Document {
    modelContext?: ModelContextLike
  }
  interface Navigator {
    modelContext?: ModelContextLike
  }
}

const text = (body: unknown): ToolResult => ({
  content: [
    { type: 'text', text: typeof body === 'string' ? body : JSON.stringify(body, null, 2) },
  ],
})

/**
 * WebMCP (webmachinelearning.github.io/webmcp) — registers tools on
 * document.modelContext so in-browser AI agents can act on the page without
 * scraping it. Chrome origin trial only; the whole thing no-ops elsewhere.
 * The only write tool is kirana_submit_inquiry, which posts to the same
 * rate-limited endpoint as the contact form and the MCP server.
 */
export function WebMcpTools({ locale }: { locale: Locale }) {
  const t = useTranslations('home.services')
  useEffect(() => {
    const ctx = document.modelContext ?? navigator.modelContext
    if (!ctx || typeof ctx.registerTool !== 'function') return

    const es = locale === 'es'
    const controllers: AbortController[] = []
    const register = (tool: WebMcpTool) => {
      const controller = new AbortController()
      controllers.push(controller)
      // Rejects on name collisions or unsupported hosts — harmless.
      void ctx.registerTool(tool, { signal: controller.signal }).catch(() => {})
    }

    register({
      name: 'kirana_get_site_overview',
      description:
        'Get the structured Kirana Labs site overview: what the company does plus its machine-readable interfaces (OpenAPI, llms.txt, schema feed).',
      inputSchema: { type: 'object', properties: {}, additionalProperties: false },
      annotations: {
        title: 'Kirana Labs site overview',
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: false,
      },
      execute: async () => text(await (await fetch('/api/agent')).json()),
    })

    register({
      name: 'kirana_list_services',
      description: 'List Kirana Labs service offerings with descriptions and links to each service page.',
      inputSchema: { type: 'object', properties: {}, additionalProperties: false },
      annotations: {
        title: 'List Kirana Labs services',
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: false,
      },
      execute: async () =>
        text(
          [
            { key: 'newBusiness', path: es ? '/es/servicios/nuevo-negocio' : '/services/new-business' },
            { key: 'enterprise', path: es ? '/es/servicios/empresa' : '/services/enterprise' },
            { key: 'ai', path: es ? '/es/servicios/ia' : '/services/ai' },
            { key: 'latam', path: es ? '/es/servicios/latam' : '/services/latam' },
          ].map((s) => ({ title: t(`${s.key}Title`), description: t(`${s.key}Desc`), url: s.path })),
        ),
    })

    register({
      name: 'kirana_list_content',
      description:
        'Get the machine-readable index of all published Kirana Labs content (blog posts and case studies) in English and Spanish.',
      inputSchema: { type: 'object', properties: {}, additionalProperties: false },
      annotations: {
        title: 'List published Kirana Labs content',
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: false,
      },
      execute: async () => text(await (await fetch('/llms.txt')).text()),
    })

    register({
      name: 'kirana_get_page_markdown',
      description:
        'Fetch the Markdown representation of any public page on this site — every page has a markdown twin.',
      inputSchema: {
        type: 'object',
        properties: {
          path: {
            type: 'string',
            description: 'Site path like "/blog" or "/es/servicios/ia". Defaults to the current page.',
          },
        },
        additionalProperties: false,
      },
      annotations: {
        title: 'Get a page as Markdown',
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: false,
      },
      execute: async (args) => {
        const path =
          typeof args.path === 'string' && args.path.startsWith('/')
            ? args.path
            : location.pathname
        return text(await (await fetch(`/api/markdown?path=${encodeURIComponent(path)}`)).text())
      },
    })

    register({
      name: 'kirana_submit_inquiry',
      description:
        'Send a project inquiry to the Kirana Labs team. Rate-limited — shares the contact form, REST API, and MCP server budgets.',
      inputSchema: {
        type: 'object',
        properties: {
          name: { type: 'string', maxLength: 120, description: 'Sender name' },
          email: { type: 'string', format: 'email', maxLength: 254, description: 'Reply-to address' },
          message: { type: 'string', maxLength: 5000, description: 'Project description or question' },
          phone: {
            type: 'string',
            maxLength: 50,
            description: 'Optional phone number with country code',
          },
          service: {
            type: 'string',
            maxLength: 120,
            description: 'Area of interest, e.g. new-business, enterprise, ai, latam',
          },
        },
        required: ['name', 'email', 'message'],
        additionalProperties: false,
      },
      annotations: {
        title: 'Send a project inquiry to Kirana Labs',
        readOnlyHint: false,
        destructiveHint: false,
        idempotentHint: false,
        openWorldHint: true,
      },
      execute: async (args) => {
        const res = await fetch('/api/inquiries', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...args, locale }),
        })
        const body = (await res.json().catch(() => ({}))) as Record<string, unknown>
        return text(res.ok ? { status: 'sent', ...body } : { status: res.status, ...body })
      },
    })

    register({
      name: 'kirana_get_scheduling_link',
      description: 'Get the link to book an introductory call with the Kirana Labs team.',
      inputSchema: { type: 'object', properties: {}, additionalProperties: false },
      annotations: {
        title: 'Get the Kirana Labs scheduling link',
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: false,
      },
      execute: async () =>
        text({
          schedulingPage: es ? '/es/reunion-de-calendly' : '/calendly-meeting',
          calendlyUrl: CALENDLY_URL,
        }),
    })

    return () => controllers.forEach((controller) => controller.abort())
  }, [locale, t])

  return null
}
